import React, { useEffect, useRef, useState } from 'react';
import api from '../utils/axios';
import { Plus, Edit, Trash2 } from 'lucide-react';
import CTable from '../components/shared/c-table';
import CModal from '../components/shared/c-modal';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

const Currencies = () => {
  // The add/edit popup opens on its first field rather than the close button.
  const codeField = useRef(null);
  const { user } = useAuth();
  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add'); // 'add' or 'edit'
  const [modalCurrency, setModalCurrency] = useState(null);
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [symbol, setSymbol] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleteCurrency, setDeleteCurrency] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchCurrencies();
  }, []);

  const fetchCurrencies = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/currencies');
      setCurrencies(res.data.data);
    } catch (err) {
      toast.error('Failed to load currencies');
    } finally {
      setLoading(false);
    }
  };

  const openAddModal = () => {
    setModalMode('add');
    setModalCurrency(null);
    setCode('');
    setName('');
    setSymbol('');
    setShowModal(true);
  };

  const openEditModal = (currency) => {
    setModalMode('edit');
    setModalCurrency(currency);
    setCode(currency.code);
    setName(currency.name);
    setSymbol(currency.symbol || '');
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { code: code.toUpperCase(), name, symbol };
      if (modalMode === 'add') {
        await api.post('/api/currencies', payload);
        toast.success('Currency added');
      } else {
        await api.put(`/api/currencies/${modalCurrency.id}`, payload);
        toast.success('Currency updated');
      }
      setShowModal(false);
      fetchCurrencies();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save currency');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/api/currencies/${deleteCurrency.id}`);
      toast.success('Currency deleted');
      setDeleteCurrency(null);
      fetchCurrencies();
    } catch (err) {
      console.error('Currency delete error:', err);

      let errorMessage = 'Failed to delete currency';

      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.message) {
        errorMessage = err.message;
      } else if (err.response?.statusText) {
        errorMessage = err.response.statusText;
      }

      toast.error(errorMessage);
    } finally {
      setDeleting(false);
    }
  };

  if (!user?.roles?.some(r => r.name === 'super_admin')) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold mb-2">Access Denied</h2>
        <p className="text-gray-600">You do not have permission to view this page.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Currencies</h1>
          <p className="text-gray-600">Manage system currencies</p>
        </div>
        <button
          data-testid="currencies-add-btn"
          onClick={openAddModal}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Currency
        </button>
      </div>

      {/* Currencies Table */}
      <CTable
        columns={[
          {
            key: 'code',
            header: 'Code',
            cellClassName: 'font-medium',
            render: (c) => <span data-testid="currencies-cell-code">{c.code}</span>,
          },
          {
            key: 'name',
            header: 'Name',
            render: (c) => c.name,
          },
          {
            key: 'symbol',
            header: 'Symbol',
            render: (c) => c.symbol,
          },
          {
            key: 'created',
            header: 'Created',
            cellClassName: 'text-gray-500',
            render: (c) => c.created_at ? new Date(c.created_at).toLocaleDateString() : '-',
          },
          {
            key: 'actions',
            header: 'Actions',
            align: 'right',
            cellClassName: 'font-medium',
            render: (c) => (
              <>
                <button
                  onClick={() => openEditModal(c)}
                  className="p-1 text-warning-600 hover:text-warning-900"
                  title="Edit"
                >
                  <Edit className="h-4 w-4" />
                </button>
                <button
                  data-testid="currencies-delete"
                  onClick={() => setDeleteCurrency(c)}
                  className="p-1 text-danger-600 hover:text-danger-900 ml-2"
                  title="Delete"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </>
            ),
          },
        ]}
        data={currencies}
        rowKey="id"
        loading={loading}
        getRowProps={(c) => ({ 'data-testid': `currencies-row-${c.id}` })}
        header={
          <h3 className="text-lg font-medium text-gray-900">
            {currencies.length} Currenc{currencies.length !== 1 ? 'ies' : 'y'}
          </h3>
        }
        emptyMessage={
          <>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No currencies found</h3>
            <p className="mt-1 text-sm text-gray-500">Get started by adding your first currency.</p>
          </>
        }
      />

      {/* Add/Edit Modal */}
      {showModal && (
        <CModal
          onClose={() => setShowModal(false)}
          labelledBy="currency-modal-title"
          initialFocus={codeField}
          dismissible={!saving}
          closeOnBackdrop={false}
          className="bg-white rounded-lg shadow-lg w-full max-w-md p-6 relative"
        >
          <button
            aria-label="Close"
            className="absolute top-3 right-3 text-gray-400 hover:text-gray-700 text-2xl font-bold"
            onClick={() => setShowModal(false)}
          >
            &times;
          </button>
          <h2 id="currency-modal-title" className="text-xl font-bold mb-4">{modalMode === 'add' ? 'Add Currency' : 'Edit Currency'}</h2>
          <form onSubmit={handleSave} className="space-y-4">
            <div>
              <label htmlFor="currency-code" className="block text-sm font-medium text-gray-700">Code</label>
              <input
                id="currency-code"
                type="text"
                ref={codeField}
                data-testid="currencies-field-code"
                className="mt-1 w-full border rounded-md px-3 py-2 uppercase"
                value={code}
                onChange={e => setCode(e.target.value)}
                placeholder="e.g. USD"
                maxLength={3}
                required
              />
              <p className="mt-1 text-xs text-gray-500">3-character uppercase code (e.g. USD, PKR, EUR)</p>
            </div>
            <div>
              <label htmlFor="currency-name" className="block text-sm font-medium text-gray-700">Name</label>
              <input
                id="currency-name"
                type="text"
                data-testid="currencies-field-name"
                className="mt-1 w-full border rounded-md px-3 py-2"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="e.g. US Dollar"
                required
              />
            </div>
            <div>
              <label htmlFor="currency-symbol" className="block text-sm font-medium text-gray-700">Symbol</label>
              <input
                id="currency-symbol"
                type="text"
                className="mt-1 w-full border rounded-md px-3 py-2"
                value={symbol}
                onChange={e => setSymbol(e.target.value)}
                placeholder="e.g. $"
                required
              />
            </div>
            <button
              type="submit"
              data-testid="currencies-submit"
              className="w-full bg-primary-600 text-white py-2 rounded-md hover:bg-primary-700 transition-colors"
              disabled={saving}
            >
              {saving ? (modalMode === 'add' ? 'Adding...' : 'Saving...') : (modalMode === 'add' ? 'Add Currency' : 'Save Changes')}
            </button>
          </form>
        </CModal>
      )}

      {/* Delete Confirmation Modal */}
      {deleteCurrency && (
        <CModal
          onClose={() => setDeleteCurrency(null)}
          labelledBy="delete-currency-title"
          dismissible={!deleting}
          className="bg-white rounded-lg shadow-lg w-full max-w-md p-6 relative"
          data-testid="currencies-confirm-modal"
        >
          <button
            aria-label="Close"
            className="absolute top-3 right-3 text-gray-400 hover:text-gray-700 text-2xl font-bold"
            onClick={() => setDeleteCurrency(null)}
          >
            &times;
          </button>
          <h2 id="delete-currency-title" className="text-xl font-bold mb-4 text-danger-700">Delete Currency</h2>
          <p className="mb-4 text-gray-700">
            Are you sure you want to delete the currency <b>{deleteCurrency.code} ({deleteCurrency.name})</b>?<br />
            <span className="text-danger-600 font-semibold">
              This action cannot be undone. You cannot delete a currency if it is in use by employees or salaries.
            </span>
          </p>
          <div className="flex justify-end gap-2">
            <button
              data-testid="currencies-confirm-no"
              className="px-4 py-2 rounded bg-gray-100 text-gray-700 hover:bg-gray-200"
              onClick={() => setDeleteCurrency(null)}
              disabled={deleting}
            >
              Cancel
            </button>
            <button
              data-testid="currencies-confirm-yes"
              className="px-4 py-2 rounded bg-danger-600 text-white hover:bg-danger-700 font-semibold"
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </CModal>
      )}
    </div>
  );
};

export default Currencies;
